import React from 'react';

function CodeMapsContent() {
  return (
    <>
      <p>Code Maps are a powerful feature in RepoPrompt that provide LLMs with structural understanding of your code without sending the entire file content, significantly saving on token usage.</p>
      
      <h3>What are Code Maps?</h3>
      <p>Code Maps extract and summarize key structural elements from your code, such as:</p>
      <ul>
        <li>Class and interface definitions</li>
        <li>Function and method signatures</li>
        <li>Type declarations</li>
      </ul>
      <p>This gives the AI a "map" of your codebase structure without needing the full implementation details.</p>
      
      <p>CodeMaps are designed to extract essential information about how to use a file from its public definitions, helping models avoid hallucinating function calls or enum definitions.</p>
      
      <h3>Supported Languages</h3>
      <p>CodeMaps currently support the following languages:</p>
      <ul>
        <li>Swift (swift)</li>
        <li>JavaScript (js)</li>
        <li>C# (cs)</li>
        <li>Python (py)</li>
        <li>C (c)</li>
        <li>Rust (rs)</li>
        <li>C++ (cpp)</li>
        <li>Go (go)</li>
        <li>Java (java)</li>
        <li>Dart (dart)</li>
        <li>TypeScript (ts, tsx)</li>
      </ul>
      
      <h3>Configuring Code Maps</h3>
      <ol>
        <li>Click the gear icon next to the "Code Map" tab to open Code Map settings</li>
        <li>Under "Code Map Usage", choose an option:
          <ul>
            <li><strong>None:</strong> No code map is included</li>
            <li><strong>Auto:</strong> (Default) RepoPrompt automatically detects definitions based on available tokens and relevance</li>
            <li><strong>Complete:</strong> Includes all definitions found in the scanned files (use with caution, can be large)</li>
          </ul>
        </li>
        <li>Under "File Tree", choose how the file structure is represented:
          <ul>
            <li><strong>Auto:</strong> Balance between token usage and context</li>
            <li><strong>Full"</strong> Include all files in the workspace</li>
            <li><strong>Selected:</strong> Only include selected files</li>
            <li><strong>None:</strong> Don't include file tree information</li>
          </ul>
        </li>
      </ol>
      
      <h3>Language Types and CodeMap Behavior</h3>
      <p>CodeMaps behave differently based on language type:</p>
      <ul>
        <li><strong>Strongly Typed Languages:</strong> For languages like TypeScript, Swift, Java, etc., the Auto mode will automatically detect references to other files and include relevant file APIs in the final prompt.</li>
        <li><strong>Dynamically Typed Languages:</strong> For languages like JavaScript or Python, the Auto mode may not pick up all references. Complete mode may be more suitable for these languages to ensure comprehensive coverage.</li>
      </ul>
      
      <h3>Benefits of Code Maps</h3>
      <ul>
        <li><strong>Token Efficiency:</strong> Significantly reduces token usage for large files</li>
        <li><strong>Structural Understanding:</strong> Helps the AI understand code organization</li>
        <li><strong>Type Awareness:</strong> Preserves type information for better code analysis</li>
        <li><strong>Expanded Context:</strong> Allows including more files within token limits</li>
      </ul>
      
      <h3>CodeMap Modes in Detail</h3>
      <ul>
        <li><strong>Auto Mode:</strong> Balances token usage and context by intelligently selecting relevant definitions.</li>
        <li><strong>Complete Mode:</strong> Includes all extracted file APIs for files that are not selected, helping models gain a complete picture of the codebase at lower resolution than the files you're working with. This is particularly useful for understanding the broader codebase structure.</li>
      </ul>
      
      <h3>Best Practices</h3>
      <ul>
        <li>Code Maps work best with strongly-typed languages (TypeScript, Swift, Java, etc.)</li>
        <li>For small files or specific implementation details, include the full file instead</li>
        <li>Use "Auto" mode for most cases to let RepoPrompt optimize token usage</li>
        <li>Use "Complete" mode when the structural relationships are complex and critical, or when working with dynamically typed languages</li>
      </ul>
      
      <div className="tip-box">
        <strong>Tip:</strong> When you're asking for high-level architectural insights or structural changes, Code Maps are often more useful than full file content, as they help the AI focus on relationships between components. Try setting codemap to complete and deselecting all files.
      </div>
    </>
  );
}

export default CodeMapsContent;